import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useGetAssignedReviewPeriodsQuery } from "src/store/api/generatedApi";
import {
  AssignedRange,
  getTotalAssignedMinutes,
  hasAnyStaleRange,
  mergeConsecutivePeriods,
} from "./utils/assignedPeriodUtils";

dayjs.extend(utc);

type AssignedReviewIndicatorProps = Readonly<{
  location: string;
}>;

export const AssignedReviewIndicator = ({
  location,
}: AssignedReviewIndicatorProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const scrapEyeMetadata = useScrapEyeMetadata();
  const expirySeconds =
    scrapEyeMetadata.label_review_config.assignment_expiry_seconds;

  const { data } = useGetAssignedReviewPeriodsQuery({
    tenantName,
    location,
  });

  const mergedRanges = useMemo(
    () => mergeConsecutivePeriods(data?.periods ?? [], expirySeconds),
    [data?.periods, expirySeconds]
  );

  const totalMinutes = getTotalAssignedMinutes(mergedRanges);
  const anyStale = hasAnyStaleRange(mergedRanges);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRangeClick = useCallback(
    (range: AssignedRange) => {
      const dateParam = dayjs.utc(range.start).format("YYYY-MM-DDTHH:mm:ss");
      void navigate(
        `/${tenantName}/tool/manual-review/${location}?date=${dateParam}`
      );
      handleClose();
    },
    [tenantName, location, navigate, handleClose]
  );

  if (totalMinutes === 0) return null;

  return (
    <>
      <Chip
        icon={<AssignmentIndIcon />}
        label={t("assignedMinutes", { minutes: totalMinutes })}
        color={anyStale ? "warning" : "info"}
        size="small"
        onClick={handleClick}
        sx={{
          cursor: "pointer",
          ...(anyStale && {
            animation: "stale-pulse 2s ease-in-out infinite",
            "@keyframes stale-pulse": {
              "0%, 100%": { boxShadow: "0 0 0 0 rgba(237, 108, 2, 0.4)" },
              "50%": { boxShadow: "0 0 8px 4px rgba(237, 108, 2, 0.3)" },
            },
          }),
        }}
      />
      <Popover
        open={anchorEl != null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("yourAssignedPeriods")}
          </Typography>
          <List dense disablePadding>
            {mergedRanges.map((range) => (
              <RangeItem
                key={`${range.start}-${range.end}`}
                range={range}
                onClick={handleRangeClick}
              />
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};

const formatRange = (range: AssignedRange): string => {
  const start = dayjs.utc(range.start);
  const end = dayjs.utc(range.end);
  const sameDay = start.format("YYYY-MM-DD") === end.format("YYYY-MM-DD");
  if (sameDay) {
    return `${start.format("MMM D, HH:mm")} – ${end.format("HH:mm")}`;
  }
  return `${start.format("MMM D, HH:mm")} – ${end.format("MMM D, HH:mm")}`;
};

const formatRemaining = (expiresAtIso: string): string => {
  const remainingMinutes = dayjs.utc(expiresAtIso).diff(dayjs.utc(), "minute");
  if (remainingMinutes <= 0) return "0m";
  if (remainingMinutes < 60) return `${remainingMinutes}m`;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  return `${hours}h ${minutes}m`;
};

const RangeItem = ({
  range,
  onClick,
}: {
  range: AssignedRange;
  onClick: (range: AssignedRange) => void;
}) => {
  const { t } = useTenantTranslation();
  const durationMinutes = dayjs
    .utc(range.end)
    .diff(dayjs.utc(range.start), "minute");

  const expiryTooltip =
    range.hasStale && range.earliestExpiresAt
      ? t("assignmentExpiresIn", {
          duration: formatRemaining(range.earliestExpiresAt),
        })
      : null;

  return (
    <ListItemButton
      onClick={() => {
        onClick(range);
      }}
      sx={{ borderRadius: 1, py: 0.5 }}
      title={t("goToPeriod")}
    >
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatRange(range)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("durationMinutes", { minutes: durationMinutes })}
            </Typography>
            {expiryTooltip ? (
              <WarningAmberIcon
                fontSize="small"
                color="warning"
                titleAccess={expiryTooltip}
              />
            ) : null}
          </Stack>
        }
      />
    </ListItemButton>
  );
};
